/**
 * Price formatting helpers.
 *
 * The platform bills exclusively in USD. The display always includes the ISO
 * currency code so a user in any locale can read "$5.00 USD" and know exactly
 * what currency they're paying in (vs. ambiguous "$" which could read as AUD,
 * CAD, NZD, SGD, MXN, etc. depending on origin).
 *
 * Numerals + currency symbol position are formatted via the user's browser
 * locale. Examples (amount = 500 cents):
 *   en-US → "$5.00 USD / month"
 *   fr-FR → "5,00 $US / month"
 *   de-DE → "5,00 $ / month"
 *   ja-JP → "$5.00 / month"
 *
 * The unit suffix ("/ month", "/ year") is plain English for now — if the app
 * later ships translated strings, swap the suffix for an i18n lookup keyed by
 * `interval`.
 */

export type BillingInterval = 'month' | 'year';

const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (locale: string): Intl.NumberFormat => {
  const key = `${locale}|USD`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'code', // "USD 5.00" — explicit, locale-positioned
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    formatterCache.set(key, f);
  }
  return f;
};

const detectLocale = (): string => {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-US';
};

const intervalSuffix = (interval: BillingInterval): string => {
  switch (interval) {
    case 'year':
      return ' / year';
    case 'month':
    default:
      return ' / month';
  }
};

/**
 * Format a USD amount (in dollars) with locale-aware numerals + ISO code +
 * billing interval suffix.
 *
 * @example
 *   formatPrice(5)             // "5.00 USD / month"  (en-US: "$5.00 USD / month")
 *   formatPrice(7, 'year')     // "7.00 USD / year"
 *   formatPrice(null)          // "—"
 */
export const formatPrice = (
  amount: number | null | undefined,
  interval: BillingInterval = 'month',
  locale: string = detectLocale(),
): string => {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return `${getFormatter(locale).format(amount)}${intervalSuffix(interval)}`;
};

/**
 * Convenience: format a Stripe-style cents amount.
 *
 * @example
 *   formatPriceCents(500)  // "$5.00 USD / month" (en-US)
 */
export const formatPriceCents = (
  cents: number | null | undefined,
  interval: BillingInterval = 'month',
  locale: string = detectLocale(),
): string => {
  if (cents == null) return '—';
  return formatPrice(cents / 100, interval, locale);
};

/**
 * CTA suffix form: space-em-dash-space + price. Used as a trailing fragment
 * on button labels like `Publish — $5.00 USD / month`.
 *
 * @example
 *   `Publish${formatPriceCta(5)}`     // "Publish — $5.00 USD / month"
 *   `Upgrade${formatPriceCta(7)}`     // "Upgrade — $7.00 USD / month"
 */
export const formatPriceCta = (
  amount: number | null | undefined,
  interval: BillingInterval = 'month',
  locale: string = detectLocale(),
): string => {
  const formatted = formatPrice(amount, interval, locale);
  if (formatted === '—') return '';
  return ` — ${formatted}`;
};

const formatScheduleDate = (iso: string, locale: string = detectLocale()): string =>
  new Date(iso).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/**
 * Subscription summary line: price + renewal/cancellation marker. Used in
 * the Subscription KV row on the site detail page.
 *
 * @example
 *   formatPriceWithSchedule(5, { periodEnd: '2026-05-27', cancelling: false })
 *   // "$5.00 USD / month · renews May 27, 2026"
 *
 *   formatPriceWithSchedule(5, { periodEnd: '2026-05-27', cancelling: true })
 *   // "$5.00 USD / month · cancels May 27, 2026"
 *
 *   formatPriceWithSchedule(5, { periodEnd: null, cancelling: true })
 *   // "$5.00 USD / month · cancelling"
 *
 *   formatPriceWithSchedule(5, {})
 *   // "$5.00 USD / month"
 */
export const formatPriceWithSchedule = (
  amount: number | null | undefined,
  options: {
    periodEnd?: string | null;
    cancelling?: boolean;
    interval?: BillingInterval;
    locale?: string;
  } = {},
): string => {
  const { periodEnd, cancelling, interval = 'month' } = options;
  const locale = options.locale ?? detectLocale();
  const base = formatPrice(amount, interval, locale);
  if (base === '—') return base;
  if (cancelling) {
    return periodEnd
      ? `${base} · cancels ${formatScheduleDate(periodEnd, locale)}`
      : `${base} · cancelling`;
  }
  if (periodEnd) {
    return `${base} · renews ${formatScheduleDate(periodEnd, locale)}`;
  }
  return base;
};
